import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Eye, EyeOff, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Grouped the way someone thinks about a sticker on a drill: what it is,
// whether it is safe to use, and who has it. The two contact rows are the
// ones worth a second thought before switching on.
const GROUPS = [
  { group: 'item', fields: ['identification', 'description', 'photos', 'manual_link'] },
  { group: 'ownership', fields: ['ownership'] },
  { group: 'safety', fields: ['compliance', 'pat', 'warranty', 'vehicle', 'checklists', 'specs'] },
  { group: 'holder', fields: ['assignment', 'responsible_person', 'responsible_contact'] },
];

/**
 * A QR sticker is readable by anyone holding the tool, including a customer's
 * representative. This decides what the page behind it reveals — no code
 * change needed to tighten or loosen it.
 */
const PublicRecordFields = () => {
  const { t } = useTranslation();
  const [fields, setFields] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inventory/public-asset-fields`);
      setFields(r.data.fields);
      setDefaults(r.data.defaults || {});
    } catch { toast.error(t('inv.public.loadFailed')); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    const previous = fields;
    setFields(next);
    try {
      const r = await axios.put(`${API}/inventory/public-asset-fields`, { fields: next });
      setFields(r.data.fields);
    } catch (e) {
      setFields(previous);
      toast.error(e.response?.data?.detail || t('inv.public.saveFailed'));
    }
    setSaving(false);
  };

  if (!fields) {
    return (
      <Card data-testid="public-record-fields-card">
        <CardHeader className="pb-3"><CardTitle className="text-base">{t('inv.public.title')}</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">{t('common.loading')}</p></CardContent>
      </Card>
    );
  }

  const shownCount = Object.values(fields).filter(Boolean).length;

  return (
    <Card data-testid="public-record-fields-card">
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base">
            {t('inv.public.titleCount', { shown: shownCount, total: Object.keys(fields).length })}
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('inv.public.hint')}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => save(defaults)} disabled={saving}
          data-testid="reset-public-fields-btn">
          <RotateCcw className="mr-1 h-3.5 w-3.5" /> {t('inv.public.reset')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {GROUPS.map(({ group, fields: keys }) => (
          <div key={group}>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t(`inv.public.groups.${group}`)}
            </h4>
            <div className="space-y-1">
              {keys.map((key) => {
                const on = !!fields[key];
                return (
                  <label key={key}
                    className="flex cursor-pointer select-none items-start gap-2.5 rounded-lg border p-2.5 transition-colors hover:bg-muted/40"
                    data-testid={`public-field-row-${key}`}>
                    <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[#AA0000]"
                      checked={on} disabled={saving}
                      onChange={(e) => save({ ...fields, [key]: e.target.checked })}
                      data-testid={`public-field-${key}`} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 text-sm font-medium">
                        {t(`inv.public.fields.${key}.label`)}
                        {on
                          ? <Eye className="h-3.5 w-3.5 text-emerald-600" />
                          : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {t(`inv.public.fields.${key}.hint`)}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PublicRecordFields;
